import React from 'react';
import { useField } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import useLocalUserPreferences from 'hooks/useLocalUserPreferences';
import type { FBModalProps } from 'ui/base/Modal';
import Modal from 'ui/base/Modal';
import type { DocumentCallback } from 'ui/base/PDFViewer/constants';

import LazyLoader from 'ui/layout/LazyLoader';

import { SUBSCRIPTION } from 'utils/formUtils';

import type { SignatureFieldValue } from './hooks/useSignZoneTypes';
import SignatureSelection from './SignatureSelection';
import { modalStyle } from './UI';

const PDFViewer = React.lazy(() => import('ui/base/PDFViewer'));

type SignatureModalProps = {
    name: string;
     path: string;
     preferenceKey: string;
} & FBModalProps;

const SignatureModal: React.FC<SignatureModalProps> = ({ name, path, preferenceKey, ...rest }) => {
  const pagePdfRef = React.useRef<HTMLCanvasElement>(null);
  const [page, setPage] = React.useState(1);
  const [displayOverlay, setDisplayOverlay] = React.useState(false);

  const { [preferenceKey]: initialState = {} } = useLocalUserPreferences();
  const { page: initialStatePage } = initialState;

  const {
    value: signatureData,
    onChange: setSignatureData
  } = useField<SignatureFieldValue>(`${name}.signatureBox`, SUBSCRIPTION.NULLABLE_VALUE_ONLY).input;
  const { page: signatureDataPage, topLeft } = signatureData ?? {};
  const hasData = (signatureDataPage ?? -1) >= 0 && topLeft?.x >= 0;

  const handleDocumentLoaded = React.useCallback((pdf: DocumentCallback) => {
    // Set the last page if there is no initial page or if the initial page is greater than the number of pages.
    if (initialStatePage === undefined || initialStatePage + 1 > pdf.numPages) {
      setPage(pdf.numPages);
    } else {
      setPage(initialStatePage + 1);
      setSignatureData(initialState);
    }
  }, [initialState, setSignatureData, initialStatePage]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    if (page !== pageNumber) {
      setDisplayOverlay(false);
    }
  };

  const handleRenderSuccessPage = () => setDisplayOverlay(true);

  return (
    <Modal
      open
      large
      className={modalStyle}
      data-cy="signature-modal"
      okButtonProps={{ disabled: !hasData, 'data-cy': 'signature-modal-confirm' }}
      {...rest}
    >
      <>
        <p className="d-flex justify-content-center">
          <FormattedMessage
            id="checkIn.mobility.contract.modal.description"
            defaultMessage="Select a page and move/adjust the signature box to where you want the customer to sign."
          />
        </p>
        <LazyLoader>
          <PDFViewer
            file={path}
            page={page}
            canvasRef={pagePdfRef}
            setPage={handlePageChange}
            onLoadSuccess={handleDocumentLoaded}
            onRenderSuccess={handleRenderSuccessPage}
            pageOverlay={displayOverlay ? <SignatureSelection name={`${name}.signatureBox`} page={page} pdfRef={pagePdfRef} /> : undefined}
          />
        </LazyLoader>
      </>
    </Modal>
  );
};

export default SignatureModal;
